import type { KeyboardEvent } from 'react';
import { useRef } from 'react';
import type React from 'react';
import TextField from '@mui/material/TextField';
import type { SxProps } from '@mui/system';
import type { Theme } from '@mui/material/styles';

interface Props {
  value: string;
  error: string | null;
  currencySymbol?: string;
  onChange: (raw: string) => number | null;
  onBlur: () => void;
  onFocus: () => void;
  customStyle?: SxProps<Theme>;
}

const ALLOWED_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Backspace', 'Delete', 'Tab', 'Enter', 'Home', 'End'];

const priceInputStyle = {
  paddingLeft: '2px',
  marginLeft: '4px',
  borderColor: 'transparent',
  borderRadius: '4px',
  width: '90px',
  minWidth: '120px',
  padding: '0px',
  '.MuiOutlinedInput-root': {
    borderRadius: '4px',
    padding: '0 0 0 2px',
  },
  '.MuiOutlinedInput-root input': {
    padding: '0 0 0 2px',
    height: '30px',
    fontSize: '14px',
    color: 'rgba(0, 0, 0, 0.85)',
    fontWeight: '400',
    fontFamily: 'Gellix',
    outlineColor: 'transparent',
  },
  '.MuiOutlinedInput-root fieldset': {
    borderColor: 'transparent',
  },
  '.MuiOutlinedInput-root:hover fieldset, .MuiOutlinedInput-root:focus-visible fieldset': {
    borderColor: 'transparent',
  },
};

const getPriceInputStyle = (hasError: boolean) => ({
  ...priceInputStyle,
  '.MuiOutlinedInput-root input': {
    ...priceInputStyle['.MuiOutlinedInput-root input'],
    outline: hasError ? 'rgb(220, 56, 86)' : 'none',
  },
  '.MuiOutlinedInput-root fieldset': {
    borderColor: hasError ? 'rgb(220, 56, 86)' : 'transparent',
  },
  '.MuiOutlinedInput-root:hover fieldset, .MuiOutlinedInput-root:focus-visible fieldset': {
    borderColor: hasError ? 'rgb(220, 56, 86)' : 'transparent',
  },
});

const PriceInput: React.FC<Props> = ({ value, error, onChange, onBlur, onFocus }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      inputRef.current?.blur();
      return;
    }
    if (ALLOWED_KEYS.includes(e.key) || e.ctrlKey || e.metaKey) return;

    const value = inputRef.current?.value ?? '';
    const selStart = inputRef.current?.selectionStart ?? value.length;
    const selEnd = inputRef.current?.selectionEnd ?? value.length;

    const next = value.slice(0, selStart) + e.key + value.slice(selEnd);

    if (!/^-?\d*\.?\d*$/.test(next.replace(/,/g, ''))) {
      e.preventDefault();
    }
  };

  return (
    <TextField
      data-testid="document-price"
      value={value}
      inputRef={inputRef}
      placeholder="0.00"
      sx={getPriceInputStyle(!!error)}
      variant="outlined"
      inputProps={{ maxLength: 24 }}
      onKeyDown={handleKeyDown}
      onChange={(e) => onChange(e.target.value)}
      onFocus={onFocus}
      onBlur={onBlur}
      error={!!error}
      className={`price-input ${error ? 'price-input__error' : ''}`}
    />
  );
};

PriceInput.displayName = 'PriceInput';
export default PriceInput;
